import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Map, Plus, Loader2, X, Send, Layers, ShieldCheck, Landmark, ToggleRight } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { StatusChip } from '../../../components/ui/StatusChip';
import {
  listMyProjects, createProject, setProjectOffplan,
  type MyProject, type ProjectKind, type ProjectStatus, type SetOffplanInput,
} from '../../../services/projectService';

// Land-Scheme (مخطط) kind is gated; with it off the page only creates regular projects.
const LAND_SCHEMES_ENABLED = import.meta.env.VITE_LAND_SCHEMES_ENABLED === 'true';
// Off-plan (بيع على الخارطة) Wafi + escrow controls are gated; backend 404s while off.
const OFFPLAN_WAFI_ENABLED = import.meta.env.VITE_OFFPLAN_WAFI_ENABLED === 'true';

const STATUS_OPTIONS: ProjectStatus[] = ['PLANNING', 'UNDER_CONSTRUCTION', 'READY', 'SOLD_OUT'];

export default function DeveloperProjectsPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const isRTL = i18n.language === 'ar';
  const token = session?.token ?? null;

  const [projects, setProjects] = useState<MyProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Create form state
  const [kind, setKind] = useState<ProjectKind>('PROJECT');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('PLANNING');
  const [totalUnits, setTotalUnits] = useState('');
  const [budgetCost, setBudgetCost] = useState('');
  const [landArea, setLandArea] = useState('');
  const [description, setDescription] = useState('');
  const [masterPlotGeometry, setMasterPlotGeometry] = useState('');

  const isScheme = LAND_SCHEMES_ENABLED && kind === 'LAND_SCHEME';

  const load = async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const res = await listMyProjects(token);
      setProjects(res.data ?? []);
    } catch (e) {
      const s = (e as { status?: number })?.status;
      setError(s === 404
        ? t('proj.noOrg', { defaultValue: 'Register a developer organization first to manage projects.' })
        : t('proj.loadError', { defaultValue: 'Could not load your projects.' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [token]);

  const resetForm = () => {
    setKind('PROJECT'); setName(''); setNameAr(''); setCity(''); setRegion('');
    setStatus('PLANNING'); setTotalUnits(''); setBudgetCost(''); setLandArea('');
    setDescription(''); setMasterPlotGeometry('');
  };

  const submit = async () => {
    if (!token || !name.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await createProject(token, {
        name: name.trim(),
        nameAr: nameAr.trim() || undefined,
        city: city.trim() || undefined,
        region: region.trim() || undefined,
        status,
        totalUnits: totalUnits ? Number(totalUnits) : undefined,
        budgetCost: budgetCost ? Number(budgetCost) : undefined,
        landArea: landArea ? Number(landArea) : undefined,
        description: description.trim() || undefined,
        kind: LAND_SCHEMES_ENABLED ? kind : undefined,
        masterPlotGeometry: isScheme && masterPlotGeometry.trim() ? masterPlotGeometry.trim() : undefined,
      });
      resetForm(); setShowForm(false);
      await load();
    } catch (e) {
      const err = e as { status?: number; details?: { code?: string; maxProjects?: number } };
      if (err?.status === 403 && err.details?.code === 'CAPABILITY_REQUIRED') {
        setError(t('proj.capabilityRequired', { defaultValue: 'Your organization needs the developer or investor capability to create projects.' }));
      } else if (err?.status === 403 && err.details?.code === 'LIMIT_REACHED') {
        setError(t('proj.limitReached', { defaultValue: 'Project limit reached for your plan. Upgrade to add more.' }));
      } else {
        setError(t('proj.createError', { defaultValue: 'Could not create the project.' }));
      }
    } finally { setBusy(false); }
  };

  const updateOffplan = async (id: string, patch: SetOffplanInput) => {
    if (!token || savingId) return;
    setSavingId(id); setError(null);
    try {
      const next = await setProjectOffplan(token, id, patch);
      setProjects((prev) => prev.map((p) => (p.id === id
        ? { ...p, wafiApproved: next.wafiApproved, offPlanSalesEnabled: next.offPlanSalesEnabled, escrow: next.escrow }
        : p)));
    } catch {
      setError(t('proj.offplanError', { defaultValue: 'Could not update off-plan settings.' }));
    } finally { setSavingId(null); }
  };

  const inputCls = 'w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500/30 transition-colors';

  // Compact off-plan eligibility panel (Wafi + escrow) — gated.
  const offplanPanel = (p: MyProject) => {
    const funded = p.escrow?.status === 'FUNDED' || p.escrow?.status === 'ACTIVE';
    const saving = savingId === p.id;
    const sellable = !!p.wafiApproved && !!p.offPlanSalesEnabled && funded;
    return (
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t('proj.offplan', { defaultValue: 'Off-plan eligibility' })}
          </span>
          {sellable
            ? <StatusChip status="READY" size="xs" label={t('proj.offplanReady', { defaultValue: 'Can sell off-plan' })} />
            : <StatusChip status="PLANNING" size="xs" label={t('proj.offplanBlocked', { defaultValue: 'Not yet eligible' })} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button" disabled={saving}
            onClick={() => updateOffplan(p.id, { wafiApproved: !p.wafiApproved })}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-50 ${
              p.wafiApproved
                ? 'border-green-300 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {p.wafiApproved ? t('proj.wafiOn', { defaultValue: 'Wafi approved' }) : t('proj.wafiOff', { defaultValue: 'Mark Wafi approved' })}
          </button>
          <button
            type="button" disabled={saving}
            onClick={() => updateOffplan(p.id, { offPlanSalesEnabled: !p.offPlanSalesEnabled })}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-50 ${
              p.offPlanSalesEnabled
                ? 'border-violet-300 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
            }`}
          >
            <ToggleRight className="w-3.5 h-3.5" />
            {p.offPlanSalesEnabled ? t('proj.offplanOn', { defaultValue: 'Off-plan on' }) : t('proj.offplanEnable', { defaultValue: 'Enable off-plan' })}
          </button>
          <button
            type="button" disabled={saving}
            onClick={() => updateOffplan(p.id, { escrowStatus: funded ? 'PENDING' : 'FUNDED' })}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-50 ${
              funded
                ? 'border-green-300 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
            }`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />}
            {funded ? t('proj.escrowFunded', { defaultValue: 'Escrow funded' }) : t('proj.escrowFund', { defaultValue: 'Mark escrow funded' })}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />
      <div className="max-w-4xl mx-auto py-8 px-4">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-gray-100 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-violet-600" /> {t('proj.title', { defaultValue: 'My projects' })}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {t('proj.subtitle', { defaultValue: 'Create and manage your developments and land schemes, then add their units.' })}
            </p>
          </div>
          {token ? (
            <button
              onClick={() => setShowForm((s) => !s)}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#004D40] text-white font-medium hover:shadow-lg transition-shadow"
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showForm ? t('common.cancel', { defaultValue: 'Cancel' }) : t('proj.new', { defaultValue: 'New project' })}
            </button>
          ) : null}
        </header>

        {error ? (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        ) : null}

        {showForm && token ? (
          <section className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 space-y-4">
            {/* Kind toggle — only when land schemes are enabled */}
            {LAND_SCHEMES_ENABLED && (
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 block">
                  {t('proj.kind', { defaultValue: 'Type' })}
                </label>
                <div className="flex gap-2">
                  {([
                    { value: 'PROJECT' as const, label: t('proj.kindProject', { defaultValue: 'Project' }), icon: Building2 },
                    { value: 'LAND_SCHEME' as const, label: t('proj.kindScheme', { defaultValue: 'Land scheme (مخطط)' }), icon: Layers },
                  ]).map((o) => {
                    const Icon = o.icon;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setKind(o.value)}
                        className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border text-sm font-medium transition-colors ${
                          kind === o.value
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder={t('proj.name', { defaultValue: 'Name (English)' })} value={name} onChange={(e) => setName(e.target.value)} maxLength={160} />
              <input className={inputCls} dir="rtl" placeholder={t('proj.nameAr', { defaultValue: 'الاسم (بالعربية)' })} value={nameAr} onChange={(e) => setNameAr(e.target.value)} maxLength={160} />
              <input className={inputCls} placeholder={t('proj.city', { defaultValue: 'City' })} value={city} onChange={(e) => setCity(e.target.value)} />
              <input className={inputCls} placeholder={t('proj.region', { defaultValue: 'Region' })} value={region} onChange={(e) => setRegion(e.target.value)} />
              <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{t(`status.${s}`, { defaultValue: s.replace(/_/g, ' ') })}</option>
                ))}
              </select>
              <input className={inputCls} type="number" min="0" placeholder={t('proj.totalUnits', { defaultValue: 'Total units' })} value={totalUnits} onChange={(e) => setTotalUnits(e.target.value)} />
              <input className={inputCls} type="number" min="0" placeholder={t('proj.budget', { defaultValue: 'Budget cost (SAR)' })} value={budgetCost} onChange={(e) => setBudgetCost(e.target.value)} />
              <input className={inputCls} type="number" min="0" placeholder={t('proj.landArea', { defaultValue: 'Land area (m²)' })} value={landArea} onChange={(e) => setLandArea(e.target.value)} />
            </div>
            <textarea className={`${inputCls} min-h-[90px]`} placeholder={t('proj.description', { defaultValue: 'Description (optional)' })} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={8000} />

            {/* Master plot geometry — only for land schemes */}
            {isScheme && (
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                  <Map className="w-4 h-4 text-violet-600" /> {t('proj.geometry', { defaultValue: 'Master plot boundary (GeoJSON)' })}
                </label>
                <textarea
                  className={`${inputCls} min-h-[90px] font-mono text-xs`} dir="ltr"
                  placeholder='{"type":"Polygon","coordinates":[[[46.6,24.7],…]]}'
                  value={masterPlotGeometry}
                  onChange={(e) => setMasterPlotGeometry(e.target.value)}
                  maxLength={200000}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {t('proj.geometryHint', { defaultValue: 'Paste the scheme outline as GeoJSON. An interactive map draw tool is coming next.' })}
                </p>
              </div>
            )}

            <button
              onClick={submit}
              disabled={!name.trim() || busy}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#004D40] text-white font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t('proj.create', { defaultValue: 'Create' })}
            </button>
          </section>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : !token ? (
          <div className="text-center text-gray-500 py-12">{t('proj.signIn', { defaultValue: 'Sign in to manage your projects.' })}</div>
        ) : projects.length === 0 ? (
          <div className="text-center text-gray-500 py-12">{t('proj.empty', { defaultValue: 'No projects yet. Create your first development or land scheme.' })}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {projects.map((p) => (
              <div key={p.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate flex items-center gap-1.5">
                      {p.kind === 'LAND_SCHEME' ? <Layers className="w-4 h-4 text-violet-500 shrink-0" /> : <Building2 className="w-4 h-4 text-violet-500 shrink-0" />}
                      {isRTL && p.nameAr ? p.nameAr : p.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[p.city, p.region].filter(Boolean).join(' · ') || t('proj.noLocation', { defaultValue: 'Location not set' })}
                    </div>
                  </div>
                  {p.status ? <StatusChip status={p.status} size="xs" /> : null}
                </div>
                <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                  <span>{t('proj.units', { defaultValue: 'Units' })}: {p.unitsCount ?? p.totalUnits ?? 0}</span>
                  {typeof p.investorsCount === 'number' ? <span>{t('proj.investors', { defaultValue: 'Investors' })}: {p.investorsCount}</span> : null}
                  {p.kind === 'LAND_SCHEME' ? (
                    <span className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-300">
                      <Layers className="w-3 h-3" /> {t('proj.scheme', { defaultValue: 'Land scheme' })}
                    </span>
                  ) : null}
                </div>
                {OFFPLAN_WAFI_ENABLED ? offplanPanel(p) : null}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
